import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserProfile } from "../../api/user";
import ConfirmModal from "../../components/ConfirmModal";

interface TopbarProps {
  toggleSidebar: () => void;
  displayName?: string;
  profilePicture?: string;
}

const Topbar: React.FC<TopbarProps> = ({
  toggleSidebar,
  displayName,
  profilePicture,
}) => {
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [userName, setUserName] = useState<string>("Admin");
  const [open, setOpen] = useState(false);
  const fetchedRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  // Keep local state in sync with props (Profile page can pass fresh values)
  useEffect(() => {
    if (displayName) setUserName(displayName);
    if (profilePicture) setAvatarUrl(profilePicture);
  }, [displayName, profilePicture]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAvatar = async () => {
      try {
        const { data } = await getUserProfile();
        const payload = data?.data || data || {};
        const url = payload.profilePictureUrl || payload.profilePicture || "";
        const name = payload.name || payload.firstName || "Admin";
        // Only set if not already provided by props
        setAvatarUrl((prev) => (profilePicture ? prev : url));
        setUserName((prev) => (displayName ? prev : name));
      } catch (e) {
        // silently ignore
      }
    };

    fetchAvatar();
  }, [displayName, profilePicture]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleConfirmLogout = () => {
    setShowConfirm(true);
  };
  const handleLogout = () => {
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  const handleProfile = () => {
    navigate("/profile");
    setOpen(false);
  };

  const fallback = "/images/dummy-profile.webp";

  return (
    <header className="flex justify-end items-center p-4 bg-white shadow-md">
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3"
        >
          <span className="text-gray-600">{userName}</span>
          <img
            src={avatarUrl || fallback}
            alt="avatar"
            className="w-8 h-8 rounded-full object-cover"
          />
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <button
              type="button"
              onClick={handleProfile}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              User Profile
            </button>
            <div className="h-px bg-gray-200" />
            <button
              type="button"
              onClick={handleConfirmLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M15.75 6.75v-1.5A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25v-1.5" />
                <path d="M12 12h9m0 0l-3-3m3 3l-3 3" />
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirm}
        title="Logout Account"
        message="Are you sure you want to logout?"
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleLogout}
        onCancel={() => setShowConfirm(false)}
      />
    </header>
  );
};

export default Topbar;
